import React, { useState } from "react";
import { sortBy } from "lodash";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterTextArea,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { RadioDropdownItem } from "@sonder/ui/source/styled/fragments/RadioDropdownItem";
import { useStore as useAuthStore } from "auth/store";
import { useRolesBasedGetCleaningCompanies } from "core/queries/GetCleaningCompanies";
import { TaskCleaningRequestCompany } from "core/queries/GetTask";

interface CleaningCompanyItemProps {
  cleaningCompany: TaskCleaningRequestCompany;
  isActive?: boolean;
  onClick: (cleaningCompany: TaskCleaningRequestCompany) => void;
}

export const CleaningCompanyItem = ({
  cleaningCompany,
  isActive,
  onClick,
}: CleaningCompanyItemProps) => {
  const label = cleaningCompany.name;
  return (
    <RadioDropdownItem
      checked={isActive}
      onClick={() => onClick(cleaningCompany)}
    >
      {label}
    </RadioDropdownItem>
  );
};

interface CleaningCompanyPickerProps {
  disabled?: boolean;
  onChange?: (cleaningCompany: TaskCleaningRequestCompany) => void;
  value?: TaskCleaningRequestCompany | null;
}

export const CleaningCompanyPicker = ({
  disabled,
  value,
  onChange,
}: CleaningCompanyPickerProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const {
    state: { user: loggedInUser },
  } = useAuthStore();
  const cityId = value?.city?.id || loggedInUser?.cityId;

  const { data, error, loading } = useRolesBasedGetCleaningCompanies({
    skip: !cityId,
    variables: {
      filters: {
        active: true,
        cityId: cityId || "",
      },
    },
  });

  const allCompanies = sortBy(data?.cleaningCompany || [], "name");
  const filteredCompanies = searchTerm
    ? allCompanies.filter(cleaningCompany =>
        cleaningCompany.name
          .toLowerCase()
          .includes(searchTerm.toLocaleLowerCase()),
      )
    : allCompanies;

  const cleaningCompanyName = value?.name;

  if (!loggedInUser?.isCleaningManager || loading || error)
    return (
      <Button disabled fullWidth type="button">
        {cleaningCompanyName || "No Company"}
      </Button>
    );

  return (
    <Dropdown
      trigger={
        <Button fullWidth disabled={disabled} type="button">
          {cleaningCompanyName || "No Company"}
        </Button>
      }
    >
      {({ isOpen: isPortalOpen, closePortal }) =>
        isPortalOpen && (
          <FilterDropdownContent align="right">
            <FilterTextArea
              autoFocus
              placeholder={`Search ${allCompanies.length} companies`}
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
              }}
            />
            {searchTerm && filteredCompanies.length === 0 && (
              <FilterEmpty>{`No Results for ${searchTerm}.`}</FilterEmpty>
            )}
            <FilterButtonList>
              {filteredCompanies.length > 0 &&
                filteredCompanies.map(cleaningCompany => (
                  <CleaningCompanyItem
                    cleaningCompany={cleaningCompany}
                    isActive={value ? value.id === cleaningCompany.id : false}
                    key={cleaningCompany.id}
                    onClick={newCompany => {
                      if (onChange && newCompany) onChange(newCompany);
                      closePortal();
                    }}
                  />
                ))}
            </FilterButtonList>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
